import moment from "moment";
import { mapGetters, mapMutations, mapActions, mapState } from "vuex";
import { country_amz_sp_api_marketplace_ids_flipped } from "@/utils/countries";
import InitializeFiltersMixin from "@/mixins/InitializeFiltersMixin.vue";
import DOMPurify from "dompurify";
import Filter from "@/store/filter";
import { CSV_EXPORT_ROW_LIMIT } from "@/utils/constants";
import { arraysAreTheSame, getCountriesMapping, isObjectEmpty, tryParse, EVENTS } from "@/utils";
import { localStorageSetItem } from "@/utils/storage";
export default {
  name: "ContentMixin",
  mixins: [InitializeFiltersMixin],
  computed: {
    isPossibleToPush() {
      const pushableStatus = ["draft", "not_accepted", "sync_error", "seller_central_not_accepted"];
      return item => {
        return pushableStatus.includes(item.statusPush.status);
      };
    },
    getDefaultFilters() {
      const defaultFilters = [{
        title: "Sellers",
        titleName: "Sellers",
        showSearch: true,
        showIcon: false,
        searchPlaceholder: "Search Seller",
        values: this.sellersList,
        selected: this.currentSellers,
        handleSelect: this.handleSellerSelect,
        handleDeselect: this.handleSellerDeselect,
        handleSearch: this.handleSellerSearch,
        handleClear: this.clearSellerFilter,
        refreshTrigger: this.refreshContentsPage
      }, {
        title: "Country",
        titleName: "Country",
        values: this.countriesList,
        showSearch: false,
        showIcon: true,
        searchPlaceholder: "Search Country",
        selected: this.currentCountries,
        handleSelect: this.handleCountrySelect,
        handleDeselect: this.handleCountryDeselect,
        handleSearch: () => {},
        handleClear: this.clearCountryFilter,
        refreshTrigger: this.refreshContentsPage
      }, {
        title: "Product",
        titleName: "Product &bull; Sku &bull; Asin",
        values: this.contentsList,
        showSearch: true,
        showIcon: false,
        searchPlaceholder: "Search Product, Sku, Asin",
        selected: this.currentContents,
        handleSelect: this.handleContentSelect,
        handleDeselect: this.handleContentDeselect,
        handleSearch: this.handleContentSearch,
        handleClear: this.clearContentFilter,
        refreshTrigger: this.refreshContentsPage
      }];
      return defaultFilters;
    },
    filterMapping() {
      return {
        Sellers: {
          list: this.sellersList,
          setList: this.sellersListSet,
          selected: this.currentSellers,
          setSelected: this.sellersSelectedSet,
          refreshList: this.refreshSellers
        },
        Country: {
          list: this.countriesList,
          setList: this.countriesListSet,
          selected: this.currentCountries,
          setSelected: this.countriesSelectedSet,
          refreshList: this.refreshCountries
        },
        Products: {
          list: this.contentsList,
          setList: this.contentSuggestionsListSet,
          selected: this.currentContents,
          setSelected: this.contentsSelectedSet,
          refreshList: this.refreshContents
        }
      };
    },
    getTabFilters() {
      const defaultFilters = this.getDefaultFilters;
      const tabFilters = [...defaultFilters];
      return tabFilters;
    },
    contentFilterData() {
      return {
        default: this.fetchContentFilterData,
        push_status_mode: this.fetchSpecifiedContentFilterData,
        push_mode: this.fetchSpecifiedContentFilterData,
        parent_view_mode: this.fetchContentFilterDataForParents
      };
    },
    contentFilterSuggestionData() {
      return {
        default: this.fetchContentFilterSuggestionsData,
        push_status_mode: this.fetchSpecifiedContentFilterSuggestionsData,
        push_mode: this.fetchSpecifiedContentFilterSuggestionsData,
        parent_view_mode: this.fetchContentFilterSuggestionDataForParents
      };
    },
    ...mapGetters(["workspaceCurrentOld", "getCountry", "getCountryFlag", "getCountryCode", "getCountry", "contentSellersListGet", "contentSellersSelectedGet", "contentCountriesListGet", "contentCountriesSelectedGet", "contentSuggestionsListGet", "contentSuggestionsSelectedGet", "contentFiltersSelectedGet", "contentChildrenSelectedGet", "contentSelectedItemGet", "contentGetGcpFilters", "modifiedContentsSelectedGet", "totalCountGet", "totalCountForParentViewGet", "contentKeyWordsGet", "contentFiltersSuggestionsGet", "contentPushStatusSelectedListGet", "contentSellersListGet", "contentSoloItemsGet"]),
    ...mapState({
      products: state => state.content.list,
      keyword_list: state => state.content.keyword_list,
      currentTab: state => state.seo.currentTabValue,
      me: state => state.auth.me
    })
  },
  methods: {
    ...mapMutations(["contentUpdateAfterPush", "contentFieldSet", "contentUpdateProcessingAmazonPushStatus", "contentSellersListSet", "contentSellersSelectedSet", "contentCountriesListSet", "contentCountriesSelectedSet", "contentSuggestionsListSet", "contentSuggestionsSelectedSet", "contentModifiedListSet", "contentsListSet", "contentTotalCountSet", "contentTotalCountForParentViewSet", "contentsModifiedListSet", "contentsKeyWordListSet", "contentSetAllChildContentIds", "contentsSellersFilterSet", "contentsMarketplacesFilterSet", "contentParentsMapSet", "contentSetParentMarketplaces", "contentParentsStringMapSet", "contentSoloItemsSet"]),
    ...mapActions(["contentGetByContentId", "contentUpdate", "contentGetByFilters", "contentSetSelectedPushStatusList", "contentGetCountries", "authStatus", "productList", "getCountries", "contentPush", "contentModifiedList", "contentIdGetByModifiedId", "contentGetParents", "contentApplySuggestedKeywords"]),
    async suggestedKeywords(modelValue) {
      //console.log("run");
      const keywords = await this.contentApplySuggestedKeywords({
        payload: {
          seller_id: modelValue.seller_id,
          asin: modelValue.asin,
          marketplace_id: modelValue.marketplace_id,
          product_name: modelValue.product_name,
          item_highlight: modelValue.item_highlight,
          product_description: modelValue.product_description,
          bullet_points: [modelValue.bullet_point_1, modelValue.bullet_point_2, modelValue.bullet_point_3, modelValue.bullet_point_4, modelValue.bullet_point_5],
          brand: modelValue.brand
        }
      });
      return keywords;
    },
    isSelectedItem(item) {
      return this.selected_contents.some(selected_content_id => item.content_id == selected_content_id);
    },
    handleFilterSelect(filterName, {
      item,
      only
    }) {
      const filter = this.filterMapping[filterName];
      if (!Array.isArray(item)) {
        item = [item];
      }
      const definedList = only ? (() => {
        this.filterMapping[filterName];
        filter.setList([...filter.list, ...filter.selected]);
        filter.setSelected([]);
        return [...filter.list];
      })() : filter.list;
      const definedListFiltered = definedList.filter(element => !item.some(current_item => element.id == current_item.id));
      filter.setList(definedListFiltered);
      filter.setSelected([...filter.selected, ...item]);
    },
    handleFilterDeselect(filterName, item) {
      const filter = this.filterMapping[filterName];
      let removedItems = [];
      if (item) {
        if (!Array.isArray(item)) {
          item = [item];
        }
        removedItems = filter.selected;
        item.forEach(innerItem => {
          const filteredSelected = filter.selected.filter(el => el.id !== innerItem.id);
          filter.setSelected(filteredSelected);
          filter.setList([...filter.list, innerItem]);
        });
      } else {
        removedItems = filter.selected;
        filter.setList([...filter.list, ...filter.selected]);
        filter.setSelected([]);
      }
      return removedItems;
    },
    resetPagingValues() {
      this.$emit("reset-paging-values");
    },
    refreshSellerDropDown(data) {
      let sellerListFilter = this.createSellerSuggestions(data);
      //sellerListFilter["A2AH6MERLA01FY"].name = "GetOmny";
      //this.contentSellersListSet(Object.values(sellerListFilter));
      this.sellersListSet(sellerListFilter.filter(seller => !this.currentSellers.some(current_seller => seller.seller_id == current_seller.seller_id)));
    },
    createSellerSuggestions(data) {
      let sellerListFilter = [];
      data.forEach(seller => {
        let found_seller_account = this.sellerAccounts.find(seller_account => seller_account.account_id === seller.seller_id || seller_account.account_id === seller.account_id);
        let singleContent = {
          id: found_seller_account.account_id,
          region: found_seller_account.region,
          name: found_seller_account.account_name || this.workspaceNameById(found_seller_account.workspace_id),
          seller_id: found_seller_account.account_id
        };
        sellerListFilter.push(singleContent);
      });
      return sellerListFilter;
    },
    refreshCountriesDropDown(data) {
      let filteredCountries = this.createCountrySuggestions(data);
      this.countriesListSet(filteredCountries.filter(country => !this.currentCountries.some(current_country => country.marketplace_id == current_country.marketplace_id)));
    },
    createCountrySuggestions(data) {
      let filteredCountries = this.countriesMapping.filter(countriesMapping => data.some(someData => someData.country_code === countriesMapping.code || someData.marketplace_id === countriesMapping.marketplace_id));
      return Object.values(filteredCountries);
    },
    refreshContentDropDown(data) {
      let contentListFilter = this.createContentSuggestions(data);
      this.contentSuggestionsListSet(contentListFilter.filter(content => !this.currentContents.some(current_content => content.content_id == current_content.content_id)));
    },
    createContentSuggestions(data) {
      let contentListFilter = [];
      data.forEach(content => {
        let singleContent = {
          id: `${content.standardized_product_name}-${content.sku}-${content.asin}`,
          product_name: content.standardized_product_name,
          seller_id: content.seller_id,
          sku: content.sku,
          asin: content.asin,
          marketplace_id: content.marketplace_id,
          main_image_link: content.standardized_main_image_link,
          content_id: content.content_id
        };
        contentListFilter.push(singleContent);
      });
      return contentListFilter;
    },
    async sellerSearch({
      value,
      refresh
    }) {
      let filter = this.filterMapping.Sellers;
      if (arraysAreTheSame(value, [])) {
        if (refresh) {
          this.resetPagingValues();
          filter.refreshList();
        }
        return;
      }
      filter.setList(this.filterMapping.Sellers.list.filter(item => value.some(aValue => item.name.toLowerCase().includes(aValue.toLowerCase()) || item.id.toLowerCase().includes(aValue.toLowerCase()))));
    },
    async contentSearch({
      value,
      refresh
    }) {
      //this.keyword_list = value;
      this.contentsKeyWordListSet(value);
      let filter = this.filterMapping.Products;
      this.resetPagingValues();
      if (arraysAreTheSame(value, [])) {
        if (refresh) {
          filter.refreshList();
        }
        return;
      }
      this.refreshContents();
    },
    async fetchContentFilterDataForParents() {
      let {
        childContentIds,
        mappedParents,
        total,
        parentMarketplaces,
        parentsStringMapSet,
        solo_items,
        suggestions
      } = await this.contentGetParents({
        ...{
          page_limit: this.perPage,
          page_offset: this.page
        },
        ...this.contentFiltersSelectedGet()
      });
      this.contentSetAllChildContentIds(childContentIds);
      this.contentSetParentMarketplaces(parentMarketplaces);
      this.contentParentsStringMapSet(parentsStringMapSet);
      this.contentSoloItemsSet(solo_items);
      mappedParents = mappedParents.map(([parent, child]) => {
        if (!parent?.is_parent_asin) parent = this.transformChildElement(parent);
        return [parent, child];
      }).sort(([parentA], [parentB]) => {
        return Number(!!parentB?.is_parent_asin) - Number(!!parentA?.is_parent_asin);
      });
      this.contentParentsMapSet(new Map(mappedParents));
      let result = {};
      if (childContentIds?.length) {
        const pagingPayload = {
          ...{
            page_limit: 2000,
            page_offset: 1
          },
          ...this.contentChildrenSelectedGet()
        };
        result = await this.contentGetByContentId(pagingPayload);
      }
      let data = this.transformProducts(result?.data ?? []);
      if (data.length >= 1) {
        data[0] = {
          ...data[0],
          total_count: total
        };
        this.contentTotalCountForParentViewSet(data);
      }
      data = data.length == 0 ? this.contentSoloItemsGet() : data;
      return {
        data,
        suggestions: suggestions ?? []
      };
    },
    async fetchContentFilterSuggestionDataForParents() {
      let {
        suggestions
      } = await this.contentGetParents({
        ...{
          page_limit: this.perPage,
          page_offset: this.page
        },
        ...this.contentFiltersSelectedGet()
      });
      return {
        suggestions: suggestions ?? []
      };
    },
    async fetchContentFilterData() {
      const pagingPayload = {
        ...{
          page_limit: this.perPage,
          page_offset: this.page
        },
        ...this.contentGetGcpFilters()
      };
      const result = await this.contentGetByFilters(pagingPayload);
      this.contentTotalCountSet(result?.data);
      return result;
    },
    async fetchContentFilterDataToDownload(pageOffset, perPage) {
      const pagingPayload = {
        ...{
          page_limit: perPage,
          page_offset: pageOffset
        },
        ...this.contentGetGcpFilters()
      };
      const result = await this.contentGetByFilters(pagingPayload);
      this.contentTotalCountSet(result?.data);
      return result.data;
    },
    async fetchContentFilterSuggestionsData() {
      const pagingPayload = {
        ...{
          page_limit: this.perPage,
          page_offset: this.page
        },
        ...this.contentFiltersSuggestionsGet()
      };
      const result = await this.contentGetByFilters(pagingPayload);
      return result;
    },
    async fetchSpecifiedContentFilterSuggestionsData() {
      const pagingPayload = {
        ...{
          page_limit: this.perPage,
          page_offset: this.page
        },
        ...this.contentFiltersSelectedGet(),
        ...{
          content_id_list: this.modified_product_contents
        }
      };
      const result = await this.contentGetByContentId(pagingPayload);
      return result;
    },
    async fetchSpecifiedContentFilterData() {
      const pagingPayload = {
        ...{
          page_limit: this.perPage,
          page_offset: this.page
        },
        ...this.contentFiltersSelectedGet(),
        ...{
          content_id_list: this.modifiedContentsSelectedGet()
        }
      };
      const result = await this.contentGetByContentId(pagingPayload);
      this.currentTab === "content_optimisation_with_parent" ? this.contentTotalCountForParentViewSet(result?.data) : this.contentTotalCountSet(result?.data);
      return result;
    },
    async refreshSellers() {
      await this.initializeSellersFilter(this.contentsSellersFilterSet);
      await this.refreshCountries(false);
    },
    handleErrorOccured() {
      this.handleError();
    },
    async getFilterValuesForSellersFilter() {
      this.sellerAccounts = await this.getOmnyAccountSellers();
      if (this.sellerAccounts.length > 0) {
        return this.sellerAccounts;
      } else {
        this.errorOccured = true;
      }
    },
    async refreshCountries(check_local_storage) {
      await this.initializeMarketplacesFilter(this.contentsMarketplacesFilterSet, check_local_storage);
      this.marketplaces_filter._key = "marketplace_id";
    },
    async getFilterValuesForMarketPlacesFilter() {
      const countries_data = await this.contentGetCountries();
      this.countriesMapping = await getCountriesMapping();
      let filteredCountries = [];
      if (countries_data) {
        filteredCountries = this.createCountrySuggestions(countries_data);
      } else {
        this.errorOccured = true;
      }
      return filteredCountries;
    },
    async refreshContentsAndSuggestions() {
      this.$emit("data-loading", true);
      //const modified_list = await this.contentModifiedList({
      //  seller_list: this.contentSellersListGet(),
      //});
      //await this.contentIdGetByModifiedId({
      //  ...this.contentFiltersSelectedGet(),
      //  ...{ content_id_list: modified_list.data },
      //});
      const content_data = await this.contentFilterData[this.$store.state.content.current_mode]();
      if (content_data) {
        let contentSuggestions = this.createContentSuggestions(content_data.suggestions);
        this.products_filter.initialize({
          list: contentSuggestions,
          selected: this.products_filter.selectedItems,
          name: "seo_content_suggestions_filter",
          key: "content_id"
        });
        this.content_list = content_data.data;
        this.contentsListSet(this.content_list);
      } else {
        this.errorOccured = true;
      }
      this.$emit("data-loading", false);
    },
    async refreshSuggestions() {
      const content_data = await this.contentFilterSuggestionData[this.$store.state.content.current_mode]();
      if (content_data) {
        let contentSuggestions = this.createContentSuggestions(content_data.suggestions);
        this.products_filter.list = contentSuggestions;
      } else {
        this.errorOccured = true;
      }
    },
    async refreshContents() {
      this.$emit("data-loading", true);
      //const modified_list = await this.contentModifiedList({
      //  seller_list: this.contentSellersListGet(),
      //});
      //await this.contentIdGetByModifiedId({
      //  ...this.contentFiltersSelectedGet(),
      //  ...{ content_id_list: modified_list.data },
      //});
      const content_data = await this.contentFilterData[this.$store.state.content.current_mode]();
      if (content_data) {
        this.content_list = content_data.data;
        this.contentsListSet(this.content_list);
      } else {
        this.errorOccured = true;
      }
      this.$emit("data-loading", false);
    },
    async refreshContentsPage() {
      await this.refreshCountries(true);
    },
    handleSellerSelect({
      item,
      only
    }) {
      this.handleFilterSelect("Sellers", {
        item,
        only
      });
    },
    async handleSellerSearch({
      value,
      refresh
    }) {
      await this.sellerSearch({
        value,
        refresh
      });
    },
    async handleSellerDeselect(value) {
      const sellers = this.handleFilterDeselect("Sellers", value);
      this.resetPagingValues();
      await this.filterMapping.Sellers.refreshList();
      this.refreshSelectedSellers(sellers);
    },
    async clearSellerFilter() {
      await this.handleSellerDeselect(null);
    },
    handleCountrySelect({
      item,
      only
    }) {
      this.handleFilterSelect("Country", {
        item,
        only
      });
    },
    handleCountryDeselect(value) {
      this.handleFilterDeselect("Country", value);
      this.resetPagingValues();
      //this.filterMapping.Products.refreshList();
    },
    clearCountryFilter() {
      this.handleCountryDeselect(null);
    },
    handleContentSelect({
      item,
      only
    }) {
      this.handleFilterSelect("Products", {
        item,
        only
      });
    },
    handleContentDeselect(value) {
      this.handleFilterDeselect("Products", value);
      this.resetPagingValues();
    },
    async handleContentSearch({
      value,
      refresh
    }) {
      await this.contentSearch({
        value,
        refresh
      });
    },
    clearContentFilter() {
      this.handleContentDeselect(null);
    },
    async clearAllFilters() {
      //this.toggleSelectAll(false);
      this.products_filter.clear();
      this.marketplaces_filter.clear();
      this.sellers_filter.clear();
      this.contentsKeyWordListSet([]);
    },
    refreshSelectedSellers(sellers) {
      sellers = sellers.map(seller => seller.id);
      const removeSellerFromSelected = (sellers, selected) => {
        return selected.filter(item => {
          return !sellers.includes(item.seller_id);
        });
      };
      this.contentsSelectedSet(removeSellerFromSelected(sellers, this.currentContents));
    },
    contentRemoveLoadingState(item) {
      this.contents_with_loading_state = this.contents_with_loading_state.filter(anId => item.content_id !== anId);
    },
    async onAmazonPush() {
      this.$emit("data-loading", true);
      await this.contentModifiedList({
        seller_list: this.contentSellersListGet()
      });
      if (this.modified_product_contents?.length > 0) {
        this.contentCurrentModeSet("push_mode");
        await this.refreshContents();
        this.contentPushModalStatusSet(true);
        this.$emit("data-loading", false);
        return;
      }
      window.$bus.trigger("showWarning", {
        visible: true,
        message: "There is no Modified content to push"
      });
      this.$emit("data-loading", false);
    },
    updateContentBeforePush(item) {
      let filtered_item = this.filteredItems.find(anItem => anItem.content_id === item.content_id);
      let all_attributes = Object.keys(item.amazon_version);
      all_attributes.forEach(attribute => {
        if (this.isOmnyVersionEmptyString(item, attribute) && ["not_accepted", "seller_central_not_accepted"].includes(filtered_item.attribute_text_styles[attribute].status)) {
          item["omny_version"][attribute] = !this.isAmazonSellerCentralVersionEmptyString(item, attribute) ? item?.amazon_seller_central_version[attribute] : "";
        }
      });
      let bullet_point_to_push = [item["omny_version"]["bullet_point_1"], item["omny_version"]["bullet_point_2"], item["omny_version"]["bullet_point_3"], item["omny_version"]["bullet_point_4"], item["omny_version"]["bullet_point_5"]];
      item["omny_version"]["bullet_point"] = bullet_point_to_push.filter(item => !!item);
      return item;
    },
    async onPushModalSubmit(list_of_content_ids_to_push) {
      //this.$emit("data-loading", true);
      this.contents_with_loading_state = list_of_content_ids_to_push;
      let payload = {
        content_list: this.productsList.filter(item => list_of_content_ids_to_push.includes(item.content_id)).map(item => this.updateContentBeforePush(item)),
        task_type: "content_push",
        workspace_id: this.current_workspace.workspace_id
      };
      const result = await this.contentPush({
        payload: payload,
        mixpanel_event_name: list_of_content_ids_to_push.length === 1 ? "userPushedSingleContent" : "userPushedMassContent"
      });
      if (result) {
        this.onPushModalCancel();
        this.executeTaskStatusHandler(result);
        this.checkTaskStatusPeriodically(this.pushTaskIdentifier);
      }
      this.pushModalActiveButton = false;
      this.pushModalSpinButton = true;
      //this.$emit("data-loading", false);
    },
    onConfirmPushSingleContent(item) {
      this.singleContentToPush = item;
      this.confirmPushSingleContent = true;
    },
    statusValidate(item, version) {
      const result = [];
      const versionData = key => {
        return version === "amazon" ? item.amazon_version[key] : this.contentToDisplay(item, key);
      };
      const title = versionData("product_name");
      result.push(this.mValidateTitle(title));
      const item_highlight = versionData("item_highlight");
      result.push(this.mValidateHighlight(item_highlight));
      const description = versionData("product_description");
      result.push(this.mValidateDescription(description));
      const keywords = versionData("generic_keyword");
      result.push(this.mValidateKeyword(keywords));
      const bullet_points = [versionData("bullet_point_1"), versionData("bullet_point_2"), versionData("bullet_point_3"), versionData("bullet_point_4"), versionData("bullet_point_5")];
      const result_bullet_points = this.statusValidateBulletPoints(bullet_points);
      const image = item.image_large;
      result.push(this.mValidateImage(image));
      const numberOfImages = item.count_images;
      result.push(this.mValidateNumberOfImage(numberOfImages));
      return [...result, ...result_bullet_points];
    },
    statusValidateBulletPoints(bullet_points) {
      const result = [];
      result.push(this.mValidateBulletPointsEmpty(bullet_points));
      bullet_points.forEach((bullet_point, index) => {
        result.push(this.mValidateBulletPointsLength(bullet_point, index + 1));
      });
      return result;
    },
    getVersionKeys() {
      //const versionKeys = ['amazon_version', 'amazon_seller_central_version','omny_version'];
      const versionKeys = ["amazon_version"];
      return versionKeys;
    },
    getVersionName(key) {
      const versionDict = {
        amazon_version: "Amazon version",
        amazon_seller_central_version: "Amazon seller central version",
        omny_version: "Omny version"
      };
      return versionDict[key];
    },
    getContentAttributesCSV() {
      let contentAttributes = {
        product_name: "product_name",
        item_highlight: "item_highlight",
        generic_keyword: "generic_keyword",
        product_description: "product_description",
        bullet_point_1: "bullet_point_1",
        bullet_point_2: "bullet_point_2",
        bullet_point_3: "bullet_point_3",
        bullet_point_4: "bullet_point_4",
        bullet_point_5: "bullet_point_5"
      };
      return contentAttributes;
    },
    extractHeadersContentDataCSV(headersMapping) {
      // Ensure data is valid and not empty
      return Object.fromEntries(new Map(
      //Object.entries(headersMapping).map((fieldName) => [fieldName, fieldName])
      Object.entries(headersMapping).map(([key, value]) => [key, value])));
    },
    throwIfError(result) {
      if (result?.status == "error" || isObjectEmpty(result) || !result) {
        throw result;
      }
    },
    saveKeyWordListToCache(keyword_list) {
      let current_workspace = tryParse(localStorage.getItem("current_workspace"));
      if (!isObjectEmpty(current_workspace)) {
        localStorageSetItem(current_workspace.workspace_id + "contentsKeyWordList" + "keywords_list_filter", JSON.stringify(keyword_list));
      }
    },
    generateFileName() {
      let filters = this.contentFiltersSelectedGet();
      let flattenedValues = Object.keys(filters).reduce((acc, key) => {
        if (filters[key].length > 0) {
          let valuesString = filters[key].join("_");
          acc.push(valuesString);
        }
        return acc;
      }, []).join("_");

      //keep it because if wee see longerfile name issue, we should activate this condition.
      // if (flattenedValues.length > 100) {
      //   const hash = crypto.createHash('sha256');
      //   hash.update(flattenedValues);
      //   flattenedValues = hash.digest('hex').substr(0, 20); // Shorten the hash to 20 characters
      // }
      return flattenedValues;
    },
    async handleSelectionDownloadTypeCSV(download_option) {
      if (download_option === "primelis_content") {
        this.downloadContentsCSV("primelis");
      } else if (download_option === "all_content") {
        this.downloadContentsCSV("all");
      }
    },
    async downloadContentsCSV(download_type) {
      try {
        let csv_headers = this.getHeadersforMappingContentDataCSV();
        const totalPages = this.totalPagetoDownload();
        if (this.totalPages < 0) {
          this.throwIfError({});
        }
        window.$bus.trigger("showSuccess", {
          visible: true,
          message: "CSV download request initiated successfully.",
          delay: 60 // Show alert less than 1 minute
        });
        this.isDownloadRunning = true;
        let fileName = `PRIMELIS_${this.current_workspace.workspace_name}_${this.me.email}_${moment().format("YYYY_MM_DD_HH:mm")}_${download_type === "primelis" ? "primelis_content_data.csv" : "all_content_data.csv"}`;
        let rowGenerationFunction = download_type === "primelis" ? this.downloadContentDataRowsOmny : this.downloadContentDataRowsAll;

        //define if you want to just show custom text but keep the internal data retrieval using keys
        let csv_headers_custom = {
          push_status: "sync_status"
        };
        let csv_string = await this.generateCsvStringWithPaginationAndCustomColumnName(csv_headers, totalPages, rowGenerationFunction, csv_headers_custom);
        this.throwIfError(csv_string);
        this.downloadCsv(csv_string, fileName);
        this.isDownloadRunning = false;
      } catch (error) {
        window.$bus.trigger("showDanger", {
          message: "Error generating or downloading CSV" + error,
          visible: true,
          delay: 10
        });
      }
    },
    async downloadContentDataRowsOmny(currentPage) {
      let result_download = await this.fetchContentFilterDataToDownload(currentPage, CSV_EXPORT_ROW_LIMIT //this.downloadParamPerPage
      );
      let contentAttributes = this.getContentAttributesCSV();
      let headersMapping = this.getHeadersforMappingContentDataCSV();

      //console.log("[downloadContentDataRowsOmny]");

      const processedOmnyContents = result_download.map(content => {
        const base = this.processContentItem(content);
        const result = {};
        Object.keys(headersMapping).forEach(key => {
          result[key] = base[key] || "";
        });
        Object.keys(contentAttributes).forEach(key => {
          if ((content.omny_version || {})[key]) {
            result[key] = content.omny_version[key];
          }
        });
        if (!base.show_omny_validation_status) {
          result["omny_score"] = "";
        }
        return result;
      });
      return processedOmnyContents;
    },
    getOmnyScore(item) {
      try {
        const rulesOmnyScore = this.statusValidate(item, "omny");
        return this.mValidateGlobalScore(rulesOmnyScore).score;
      } catch (error) {
        console.error("Error processing Omny contents:", error);
        return "N/A";
      }
    },
    getAmazonScore(item) {
      try {
        const rulesAmzScore = this.statusValidate(item, "amazon");
        return this.mValidateGlobalScore(rulesAmzScore).score;
      } catch (error) {
        console.error("Error processing amazon contents:", error);
        return "N/A";
      }
    },
    async downloadContentDataRowsAll(currentPage) {
      let result_download = await this.fetchContentFilterDataToDownload(currentPage, CSV_EXPORT_ROW_LIMIT //this.downloadParamPerPage
      );
      let contentAttributes = this.getContentAttributesCSV();
      let headersMapping = this.getHeadersforMappingContentDataCSV();
      let processedOmnyContents = result_download.map(content => {
        let result = {};
        const base = this.processContentItem(content);
        Object.keys(headersMapping).forEach(key => {
          result[key] = base[key] || "";
        });

        // Prepare the version objects, defaulting to empty objects if undefined
        const omnyVersion = content.omny_version || {};
        const amazonSellerCentralVersion = content.amazon_seller_central_version || {};
        const amazonVersion = content.amazon_version || {};

        // Update result with the first available version data for specified attributes
        Object.keys(contentAttributes).forEach(key => {
          if ((omnyVersion || {})[key]) {
            result[key] = omnyVersion[key];
          } else if ((amazonSellerCentralVersion || {})[key]) {
            result[key] = amazonSellerCentralVersion[key];
          } else if ((amazonVersion || {})[key]) {
            result[key] = amazonVersion[key];
          }
        });
        if (!base.show_omny_validation_status) {
          result["omny_score"] = "";
        }
        return result;
      });
      return processedOmnyContents;
    },
    totalPagetoDownload() {
      return Math.ceil(this.total_count / CSV_EXPORT_ROW_LIMIT); // this.downloadParamPerPage
    },
    getAttributeTextStyles(item) {
      const attributes = ["product_name", "bullet_point_1", "bullet_point_2", "bullet_point_3", "bullet_point_4", "bullet_point_5", "generic_keyword", "product_description", "item_highlight"];
      const attributeTextStyles = {};
      attributes.forEach(attr => {
        attributeTextStyles[attr] = this.contentToDisplayTextStyle(item, attr);
      });
      return attributeTextStyles;
    },
    contentShowOmnyValidationStatusFromStyles(attributeTextStyles) {
      return Object.values(attributeTextStyles).some(styleObj => styleObj?.style === "color:#5a52ae;font-weight:660");
    },
    calculatePushStatus(item, attributeTextStyles) {
      let statusPush = this.contentPushIconToDisplay(item, attributeTextStyles);
      const statusMap = {
        unknown: "",
        draft: "Draft",
        processing: "Processing",
        sync_finished: "Sync with Amazon in progress",
        accepted: "Synced with Amazon",
        not_accepted: "Mismatch with Amazon",
        seller_central_not_accepted: "Mismatch with Seller/Vendor Central",
        sync_error: "Sync with Seller/Vendor Central failed"
      };
      return statusPush ? statusMap[statusPush.status] ?? statusPush.status : null;
    },
    processContentItem(content) {
      const attributeTextStyles = this.getAttributeTextStyles(content);
      const country = this.getCountry(content.marketplace_id);
      const productImage = this.getProductImage(content.image_list);
      return {
        listing_status: content.listing_status,
        seller_id: content.seller_id,
        marketplace_id: content.marketplace_id,
        product_type: content.product_type,
        image_small: productImage?.link,
        sku: content.sku,
        asin: content.asin,
        content_id: content.content_id,
        amazon_link: content.amazon_link,
        category_name: content.category_name,
        category_id: content.category_id,
        has_a_plus_content: content.has_a_plus_content,
        brand: content.brand,
        product_name: this.contentToDisplay(content, "product_name"),
        product_name_style: attributeTextStyles["product_name"],
        product_name_icon: this.contentIconToDisplay(content, "product_name"),
        item_highlight: this.contentToDisplay(content, "item_highlight"),
        item_highlight_style: attributeTextStyles["item_highlight"],
        item_highlight_icon: this.contentIconToDisplay(content, "item_highlight"),
        count_images: content.count_images,
        count_images_style: this.mValidateNumberOfImage(content.count_images),
        bullet_point_1: this.contentToDisplay(content, "bullet_point_1"),
        bullet_point_1_style: attributeTextStyles["bullet_point_1"],
        bullet_point_1_icon: this.contentIconToDisplay(content, "bullet_point_1"),
        bullet_point_2: this.contentToDisplay(content, "bullet_point_2"),
        bullet_point_2_style: attributeTextStyles["bullet_point_2"],
        bullet_point_2_icon: this.contentIconToDisplay(content, "bullet_point_2"),
        bullet_point_3: this.contentToDisplay(content, "bullet_point_3"),
        bullet_point_3_style: attributeTextStyles["bullet_point_3"],
        bullet_point_3_icon: this.contentIconToDisplay(content, "bullet_point_3"),
        bullet_point_4: this.contentToDisplay(content, "bullet_point_4"),
        bullet_point_4_style: attributeTextStyles["bullet_point_4"],
        bullet_point_4_icon: this.contentIconToDisplay(content, "bullet_point_4"),
        bullet_point_5: this.contentToDisplay(content, "bullet_point_5"),
        bullet_point_5_style: attributeTextStyles["bullet_point_5"],
        bullet_point_5_icon: this.contentIconToDisplay(content, "bullet_point_5"),
        generic_keyword: this.contentToDisplay(content, "generic_keyword"),
        generic_keyword_style: attributeTextStyles["generic_keyword"],
        generic_keyword_icon: this.contentIconToDisplay(content, "generic_keyword"),
        product_description: this.contentToDisplay(content, "product_description"),
        product_description_style: attributeTextStyles["product_description"],
        product_description_icon: this.contentIconToDisplay(content, "product_description"),
        push_status: this.calculatePushStatus(content, attributeTextStyles),
        last_push_date: this.contentLastPushDate(content).text,
        amazon_score: this.getAmazonScore(content),
        omny_score: this.getOmnyScore(content),
        seller_name: this.getSellerName(content.seller_id),
        country_code: this.getCountryCode(country),
        show_omny_validation_status: this.contentShowOmnyValidationStatusFromStyles(attributeTextStyles),
        parent_asins: content.parent_asins
      };
    },
    getSellerName(sellerId) {
      let seller = this.sellersFromFilter?.filterState?.selected.find(item => item.id === sellerId);
      if (seller) {
        return seller.name;
      }
      seller = this.sellersFromFilter.filterState.list.find(item => item.id === sellerId);
      return seller ? seller.name : "Unknown Seller"; // Return 'Unknown Seller' if not found
    },
    getProductImage(imageList) {
      const priorityOrder = ["image_small", "image_medium", "image_large"];
      return imageList.sort((a, b) => priorityOrder.indexOf(a.image_size) - priorityOrder.indexOf(b.image_size)).find(img => img.variant === "MAIN");
    }
  }
};